import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import {
    createColumnMenu,
    formatLastUpdatedDate,
    renderlastUpdatedTooltipHeader,
    renderTooltipHeader
} from '../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper';
import { FluentAvatar } from "../../../../../components/FluentUI/Fluent-Avatar";
import { process } from "@progress/kendo-data-query";
import { Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogContent, DialogBody, Button } from "@fluentui/react-components";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import "./../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log.scss";
import { getLastUpdatedDateColorWeeks, NameAndNoDealsBadge } from "../../../dashboard-util/dashboard-utils";
import {
    getEditedRowsStaffer,
    getJuniorBankersColumns,
    handleStafferDealJuniorBankerExport,
    makeDecimalCellHelperStaffer,
    mapEnergyDrillDownExportData,
    DealResourceEmptyState,
    getSectorColor
} from "../../dashboard-energy-banker/energy-banker-time-log/energy-banker-time-log-helper";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { SaveEnergyBankerHoursStaffer, SaveSIBankerHoursStaffer } from "../../../../../services/api/dashboard-api";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { FluentTooltip } from "../../../../../components/FluentUI/Fluent-Tooltip";
import { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { LoginContext } from "../../../../../services/api/authentication-api";
import PropTypes from "prop-types";

const DealResourceModal = ({
    show,
    onHide,
    item,
    sectorNames,
    fetchStafferDealDetailsandResourcingLog,
    fetchEnergyJuniorStaffingIndex,
    setIsEnergyJuniorStaffingIndexLoading,
    setIsEnergyStafferDealResourceLogLoading,
    fetchEnergyTechStafferTimeLog,
    setIsEnergyStafferTimeLogLoading,
    showToast,
    isSI = false
}) => {
    const user = useContext(LoginContext);
    const [juniorBankerState, setJuniorBankerState] = useState({ sort: [], filter: null });
    const [dataToSubmit, setDataToSubmit] = useState(() => 
        (item?.JuniorBankers || []).map(banker => ({
            ...banker,
            IsOverdue: item?.IsOverdue || false,
            IsProjectMember: item?.IsProjectMember,
            Members: item?.Members
        }))
    );
    const [juniorbankerGrid, setJuniorbankerGrid] = useState(process(dataToSubmit, juniorBankerState));
    const [isSaving, setIsSaving] = useState(false);
    const _juniorBankersDrillDownExport = useRef(null);
    const columnMenu = createColumnMenu(dataToSubmit);
    const originalData = useMemo(() => (item?.JuniorBankers || []).map(banker => ({
        ...banker,
        IsOverdue: item?.IsOverdue || false,
        IsProjectMember: item?.IsProjectMember,
        Members: item?.Members
    })), [item?.JuniorBankers, item?.IsOverdue, item?.IsProjectMember, item?.Members]);

    useEffect(() => {
        document.body.classList.toggle("energy-dialog-open", show);
    }, [show]);

    useEffect(() => {
        setDataToSubmit(
            (item?.JuniorBankers || []).map(banker => ({
                ...banker,
                IsOverdue: item?.IsOverdue || false,
                IsProjectMember: item?.IsProjectMember,
                Members: item?.Members
            }))
        );
    }, [item]);

    useEffect(() => {
        setJuniorbankerGrid(process(dataToSubmit, juniorBankerState));
    }, [dataToSubmit, juniorBankerState]);

    const isEmpty = dataToSubmit.length === 0;
    const finalGridData = isEmpty
        ? [{ isEmptyRow: true }]
        : juniorbankerGrid;

    const makeDecimalCell = useMemo(() => makeDecimalCellHelperStaffer(item?.ProjectId, item?.IsProjectMember, user?.userID), [item?.ProjectId, item?.IsProjectMember, user?.userID]);

    const LastWeekCell = useMemo(
        () => makeDecimalCell(),
        [makeDecimalCell]
    );
    const WeekendCell = useMemo(
        () => makeDecimalCell(),
        [makeDecimalCell]
    );
    const CurrentWeekHoursCell = useMemo(
        () => makeDecimalCell(),
        [makeDecimalCell]
    );
    const onItemChange = (e) => {
        const { field, value, dataItem } = e;
        if (value < 0 && value !== null) return;

        setDataToSubmit((prev) =>
            prev.map((row) =>
                row.TaskId === dataItem.TaskId ? { ...row, [field]: value } : row
            )
        );
    };

    const columnIsFiltered = useCallback(
        (fieldName) => isFilteredColumn(fieldName, juniorBankerState.filter),
        [juniorBankerState.filter]
    );

    const isUserMember = item?.IsProjectMember ?? false;

    const handleSubmit = async () => {
        const editedRows = getEditedRowsStaffer(dataToSubmit, originalData);
        if (editedRows.length === 0) return;
        var payload = editedRows.map(
            ({ AnalystId, TaskId, LastWeekHours, LastWeekendHours, CurrentWeekHours }) => ({
                AnalystId,
                TaskId,
                LastWeekHours,
                LastWeekendHours,
                CurrentWeekHours,
            }));
        setIsSaving(true);
        setIsEnergyStafferDealResourceLogLoading(true);
        setIsEnergyStafferTimeLogLoading(true);
        setIsEnergyJuniorStaffingIndexLoading(true);
        try {
            const saveEndpoint = isSI ? SaveSIBankerHoursStaffer : SaveEnergyBankerHoursStaffer;
            const response = await saveEndpoint(payload);
            if (!response?.data) {
                showToast("Save Failed", "Could not save your changes.", "error");
                setIsSaving(false);
                setIsEnergyStafferDealResourceLogLoading(false);
                setIsEnergyStafferTimeLogLoading(false);
                setIsEnergyJuniorStaffingIndexLoading(false);
                return;
            }
            else {
                const now = new Date();
                setIsSaving(false);
                showToast(
                    "Edits Saved Successfully",
                    `Changes saved on ${now.toLocaleDateString()} at ${now.toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                    })}`,
                    "success"
                );
                fetchStafferDealDetailsandResourcingLog()
                    .then(() => {
                        setIsEnergyStafferDealResourceLogLoading(false)
                    });
                fetchEnergyTechStafferTimeLog()
                    .then(() => {
                        setIsEnergyStafferTimeLogLoading(false);
                    });
                fetchEnergyJuniorStaffingIndex()
                    .then(() => {
                        setIsEnergyJuniorStaffingIndexLoading(false);
                    });
            }
        } catch (err) {
            setIsSaving(false);
            setIsEnergyStafferDealResourceLogLoading(false);
            setIsEnergyStafferTimeLogLoading(false);
            setIsEnergyJuniorStaffingIndexLoading(false);
            showToast("Save Failed", err?.message ?? "An unexpected error occurred.", "error");
        }
    };

    return (
        <>
            <Dialog open={show} modalType="modal" data-testid="deal-resource-modal"
                onOpenChange={(event, data) => {
                    if (!data.open) {
                        onHide();
                    }
                }}>
                <DialogSurface aria-describedby={undefined} className="deal-resource-modal">
                    <DialogBody>
                        <DialogTitle className="d-flex justify-content-between "
                            action={
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="subtle"
                                        aria-label="close"
                                        icon={<FluentUiIcon name="DismissRegular" />}
                                    />
                                </DialogTrigger>
                            }
                        >
                            <div>
                                <div>
                                    {item?.ProjectIdString && (
                                        <FluentBadge
                                            shape="rounded"
                                            appearance="tint"
                                            size="medium"
                                            text={item.ProjectIdString}
                                            className={getSectorColor(item?.Sector, sectorNames)}
                                        />
                                    )}
                                </div>
                                <div className="modal-deal-title-text">{item?.Company} • {item?.ProjectName}</div>
                                <p className="subDepartmentTitle">Deal Details And Resourcing Log</p>
                            </div>
                            <div className="d-flex align-items-start">
                                <FluentTooltip content="Export Data" target={
                                    <FluentButton
                                        appearance="secondaryWithOutline"
                                        disabled={isEmpty}
                                        icon={
                                            <FluentUiIcon
                                                disabled={isEmpty}
                                                name="ArrowDownloadRegular"
                                                color="#0F6CBD"
                                                size={20}
                                            />
                                        }
                                        onClick={() =>
                                            handleStafferDealJuniorBankerExport({ jBRef: _juniorBankersDrillDownExport, ProjectName: item?.ProjectName, Company: item?.Company })
                                        }
                                    />
                                }>
                                </FluentTooltip>
                                <FluentButton
                                    id="tb-submit-btn"
                                    text="Submit"
                                    appearance="primary"
                                    className={"ml-2"}
                                    icon={
                                        <FluentUiIcon
                                            name="DocumentArrowUpRegular"
                                            size={20}
                                            color={isSaving ? "#A0A0A0" : "#FFFFFF"}
                                        />
                                    }
                                    onClick={handleSubmit}
                                    disabled={isSaving || !item?.IsProjectMember || isEmpty}
                                />
                            </div>
                        </DialogTitle>

                        <DialogContent>
                            <div className="detail-grid-scroll-container ">
                                <Grid
                                    data={finalGridData}
                                        dataItemKey={"TaskId"}
                                        sort={juniorBankerState.sort}
                                    filter={juniorBankerState.filter}
                                    onSortChange={(e) => {
                                        setJuniorBankerState({ ...juniorBankerState, sort: e.sort })
                                        setJuniorbankerGrid(process(dataToSubmit, { ...juniorBankerState, sort: e.sort }));
                                    }}
                                    onFilterChange={(e) => {
                                        setJuniorBankerState({ ...juniorBankerState, filter: e.filter })
                                        setJuniorbankerGrid(process(dataToSubmit, { ...juniorBankerState, filter: e.filter }));
                                    }}
                                    editable="true"
                                    editField="inEdit"
                                    sortable={true}
                                    onItemChange={onItemChange}
                                    navigable={true}
                                    keyboardNavigation={true}
                                    resizable={true}
                                >
                                    <Column
                                        field="Banker"
                                        title={renderTooltipHeader("Banker")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("Banker") ? "filtered" : ""}
                                        cell={(props) => 
                                            props.dataItem.isEmptyRow ? (
                                                <td colSpan={8} className="k-table-td">
                                                    <DealResourceEmptyState 
                                                        isMember={isUserMember} 
                                                        projectId={item?.ProjectId}
                                                    />
                                                </td>
                                            ) : (
                                                <td className="background-white k-table-td ">
                                                    <div className="dashboard-username">
                                                        <FluentAvatar size={28} name={props.dataItem.Banker} className="task-member-user mr-2" />
                                                        <NameAndNoDealsBadge name={props.dataItem.Banker ?? '—'} />
                                                    </div>
                                                </td>
                                            )
                                        }
                                    />
                                    <Column
                                        field="Level"
                                        title={renderTooltipHeader("Level")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("Level") ? "filtered" : ""}
                                        cell={(props) => !props.dataItem.isEmptyRow && (
                                            <td className="background-white k-table-td">
                                                <span>{props.dataItem.Level ?? "—"}</span>
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="LastWeekHours"
                                        title={renderTooltipHeader("Last Wk Hrs(M-F)")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("LastWeekHours") ? "filtered" : ""}
                                        cell={(props) => !props.dataItem.isEmptyRow && LastWeekCell(props)}
                                    />
                                    <Column
                                        field="LastWeekendHours"
                                        title={renderTooltipHeader("Last Wknd Hrs(S-S)")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("LastWeekendHours") ? "filtered" : ""}
                                        cell={(props) => !props.dataItem.isEmptyRow && WeekendCell(props)}
                                    />
                                    <Column
                                        field="ActualTotalHours"
                                        title={renderTooltipHeader("Actual Total Hrs")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("ActualTotalHours") ? "filtered" : ""}
                                        cell={(props) => !props.dataItem.isEmptyRow && (
                                            <td className="background-white k-table-td">
                                                <span>{props.dataItem.ActualTotalHours ?? "—"}</span>
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="PriorWeekEstimate"
                                        title={renderTooltipHeader("Prior Wk Est.")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("PriorWeekEstimate") ? "filtered" : ""}
                                        cell={(props) => !props.dataItem.isEmptyRow && (
                                            <td className="background-white k-table-td">
                                                <span>{props.dataItem.PriorWeekEstimate ?? "—"}</span>
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="CurrentWeekHours"
                                        title={renderTooltipHeader("Current Wk Hrs")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("CurrentWeekHours") ? "filtered" : ""}
                                        cell={(props) => !props.dataItem.isEmptyRow && CurrentWeekHoursCell(props)}
                                    />
                                    <Column
                                        field="LastUpdated"
                                        title={renderlastUpdatedTooltipHeader("Last Updated")}
                                        columnMenu={columnMenu}
                                        filter="date"
                                        headerClassName={columnIsFiltered("LastUpdated") ? "filtered" : ""}
                                        cell={(props) => !props.dataItem.isEmptyRow && (
                                            <td className="background-white k-table-td" style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated) }}>
                                                <span>{formatLastUpdatedDate(props.dataItem.LastUpdated)}</span>
                                            </td>
                                        )}
                                    />
                                </Grid>
                            </div>
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <ExcelExport data={mapEnergyDrillDownExportData(item?.JuniorBankers, item?.Company, item?.ProjectName, item?.ProjectId)} ref={_juniorBankersDrillDownExport}>
                {getJuniorBankersColumns()}
            </ExcelExport>
        </>
    );
};

DealResourceModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    item: PropTypes.shape({
        Company: PropTypes.string,
        ProjectName: PropTypes.string,
        ProjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        JuniorBankers: PropTypes.arrayOf(PropTypes.object),
        IsProjectMember: PropTypes.bool,
        IsOverdue: PropTypes.bool,
        Members: PropTypes.arrayOf(PropTypes.object)
    }),
    fetchStafferDealDetailsandResourcingLog: PropTypes.func.isRequired,
    setIsEnergyStafferDealResourceLogLoading: PropTypes.func.isRequired,
    fetchEnergyTechStafferTimeLog: PropTypes.func.isRequired,
    setIsEnergyStafferTimeLogLoading: PropTypes.func.isRequired,
    showToast: PropTypes.func.isRequired,
    sectorNames: PropTypes.arrayOf(PropTypes.string),
    isSI: PropTypes.bool,
    fetchEnergyJuniorStaffingIndex: PropTypes.func.isRequired,
    setIsEnergyJuniorStaffingIndexLoading: PropTypes.func.isRequired,
};

export default DealResourceModal;